// Borderless editorial tooltip for chart hover state. Rendered in HTML
// (not SVG) so we get tabular-nums, font fallbacks, and the existing
// surface utility class for consistent cards across the app.
//
// Design rules from the brief:
//   - 1px border-border/60 hairline (NO drop shadow, NO border-radius > 4px).
//   - Header row: small uppercase label (e.g. "CMC 3" / "Turn 7") + total.
//   - Body rows: swatch · label · value. Sorted descending BY THE CALLER.
//   - Tabular figures throughout.
//   - Position: caller passes (cx, cy) — anchored at top-of-line via
//     translate(-50%, calc(-100% - offset)).

import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { SIZE } from "./tokens";

export interface TooltipRow {
  /** Stable key for React reconciliation. */
  key: string;
  /** Series swatch color (hex). Rendered as a 8x8 square, no border. */
  swatch: string;
  /** Series label (e.g. "Creature", "Deck A"). */
  label: string;
  /** Pre-formatted value string (e.g. "12", "0.5", "20"). Caller does the
   *  formatting because some charts show pips with decimals and some show
   *  integers — the tooltip stays format-agnostic. */
  value: string;
}

export interface ChartTooltipProps {
  /** Header line (e.g. "CMC 3", "Turn 7"). */
  header: ReactNode;
  /** Optional secondary header (e.g. "12 cards", "Deck A won"). */
  subheader?: ReactNode;
  /** Body rows, sorted descending BY THE CALLER. */
  rows: TooltipRow[];
  /** CSS-pixel left position of the anchor (typically the hover point's x). */
  cx: number;
  /** CSS-pixel top position of the anchor (typically the hover point's y). */
  cy: number;
  /** Optional extra className for the root. */
  className?: string;
}

export function ChartTooltip({
  header,
  subheader,
  rows,
  cx,
  cy,
  className,
}: ChartTooltipProps) {
  return (
    <div
      role="tooltip"
      className={cn(
        // pointer-events-none so the tooltip doesn't steal hover from the
        // chart surface; absolute positioning relative to the chart's
        // own positioned wrapper.
        "pointer-events-none absolute z-10 surface px-2 py-1.5",
        // Override .surface's rounded-lg for a tighter editorial corner.
        // surface gives us border + bg; we tone it down to /60 hairline
        // without dropping the variable.
        "rounded-[3px] border-border/60",
        className,
      )}
      style={{
        left: cx,
        top: cy,
        transform: `translate(-50%, calc(-100% - ${SIZE.tooltipOffsetY}px))`,
        minWidth: SIZE.tooltipMinWidth,
        // Explicitly drop the .surface shadow if any card recipe ever adds it.
        boxShadow: "none",
      }}
    >
      <div className="flex items-baseline justify-between gap-2 text-[11px] font-mono uppercase tracking-wider text-fg-subtle">
        <span>{header}</span>
        {subheader !== undefined && (
          <span className="font-mono normal-case tracking-normal text-fg/80 tabular-nums">
            {subheader}
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {rows.map((r) => (
            <li
              key={r.key}
              className="flex items-center justify-between gap-3 text-[12px]"
            >
              <span className="inline-flex items-center gap-1.5 text-fg">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2"
                  style={{ background: r.swatch, borderRadius: 1 }}
                />
                {r.label}
              </span>
              <span className="font-mono font-semibold tabular-nums text-fg">
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ChartTooltip;
